import { useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { AgentCard } from './AgentCard'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface AgentItem {
  readonly to: string
  readonly logo: string
  readonly logoAlt: string
  readonly title: string
  readonly description: string
  readonly accentColor: 'iris' | 'atlas' | 'cronos' | 'talos'
}

interface AgentCarouselProps {
  readonly agents: readonly AgentItem[]
}

export function AgentCarousel({ agents }: AgentCarouselProps) {
  const swiperRef = useRef<SwiperType | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const updateNavState = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      const swiper = swiperRef.current
      if (!swiper) return
      e.preventDefault()
      const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY
      swiper.translateTo(swiper.translate - delta, 0)
      updateNavState(swiper)
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [])

  const navBtnClass =
    'absolute top-1/2 -translate-y-1/2 z-10 px-2 flex items-center justify-center w-10 h-[175px] enabled:hover:bg-gray-500/5 rounded-full text-current transition-opacity duration-200 cursor-pointer opacity-50 hover:opacity-90 disabled:opacity-20 disabled:cursor-default md:block hidden'

  return (
    <div ref={containerRef} className="w-full max-w-6xl mx-auto md:px-4 relative">
      <button
        onClick={() => swiperRef.current?.slidePrev()}
        className={`${navBtnClass} -left-8`}
        aria-label="Previous"
        disabled={isBeginning}
      >
        <ChevronLeft />
      </button>

      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper
          updateNavState(swiper)
        }}
        onSlideChange={updateNavState}
        onProgress={updateNavState}
        modules={[FreeMode]}
        freeMode={{ enabled: true, momentum: true }}
        breakpoints={{
          0: { slidesPerView: 1.2 },
          740: { slidesPerView: 2.2 },
          1024: { slidesPerView: 3.2 },
        }}
        className="my-8 h-[175px] md:h-[175px]"
      >
        {agents.map((agent) => (
          <SwiperSlide key={agent.to} className="flex p-3">
            <AgentCard
              to={agent.to}
              logo={agent.logo}
              logoAlt={agent.logoAlt}
              title={agent.title}
              description={agent.description}
              accentColor={agent.accentColor}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <button
        onClick={() => swiperRef.current?.slideNext()}
        className={`${navBtnClass} -right-8`}
        aria-label="Next"
        disabled={isEnd}
      >
        <ChevronRight />
      </button>
    </div>
  )
}
